import React from "react";
import { MailIcon } from "lucide-react";
import { BRAND_THEME } from "../ui/brandTheme";
import {
  PAGE_HEADER_ROW,
  PAGE_SHELL,
  PAGE_SUBTITLE,
} from "../ui/pageChrome";

const SUPPORT_EMAIL = "kornivus.infotech@gmail.com";
const CARD = `bg-white rounded-xl border p-5 sm:p-6 shadow-sm ${BRAND_THEME.section.wrap}`;
const CTA = `inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold rounded-lg transition-colors shrink-0 ${BRAND_THEME.button.primary}`;

const STEPS = [
  {
    n: "1",
    title: "Introduce an agency",
    text: "Send us the organization name and a decision-maker we may contact. A short email is enough.",
  },
  {
    n: "2",
    title: "We run the conversation",
    text: "Kornivus walks them through charts, episodes, scheduling, and compliance — you do not need to demo the product.",
  },
  {
    n: "3",
    title: "Reward after they go live",
    text: "When that agency becomes an active Kornivus client, you receive a $1,000 referral thank-you.",
  },
];

const ReferralProgram = () => (
  <div className={PAGE_SHELL}>
    <div className={PAGE_HEADER_ROW}>
      <div>
        <h1 className={`text-2xl font-bold ${BRAND_THEME.section.title}`}>
          Referral program
        </h1>
        <p className={PAGE_SUBTITLE}>
          Help another home health or hospice agency run on Kornivus. Earn $1,000
          when they become a client.
        </p>
      </div>
    </div>

    <section className={`${CARD} overflow-hidden p-0`}>
      <div className="relative h-44 sm:h-52 overflow-hidden bg-indigo-100">
        <img
          src="/promo/patient-visit.jpg"
          alt="Home health clinician visiting a patient"
          className="h-full w-full object-cover object-[center_30%]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/20 to-transparent" />
        <div className="absolute bottom-4 left-5 right-5 text-white">
          <p className="text-3xl font-bold tracking-tight">$1,000</p>
          <p className="mt-0.5 text-sm text-white/90">
            per agency that goes live after your introduction
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-indigo-100">
        {STEPS.map((step) => (
          <div key={step.n} className="p-5 sm:p-6">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">
              {step.n}
            </span>
            <h2 className="mt-3 text-sm font-semibold text-slate-900">
              {step.title}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              {step.text}
            </p>
          </div>
        ))}
      </div>
    </section>

    <section className={CARD}>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        <div>
          <p className={`text-[11px] font-semibold uppercase tracking-[0.2em] ${BRAND_THEME.section.title}`}>
            Send a referral
          </p>
          <p className="mt-2 text-lg font-semibold text-slate-900">
            Email our team with the agency details
          </p>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">
            Include the agency name, city, and a contact if you have one. Mention
            that you are referring them through the Kornivus referral program.
          </p>
        </div>
        <a href={`mailto:${SUPPORT_EMAIL}`} className={CTA}>
          <MailIcon className="w-4 h-4" />
          {SUPPORT_EMAIL}
        </a>
      </div>
    </section>
  </div>
);

export default ReferralProgram;
